import type {
  MobileGraphqlEndpoint,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { ARTIFACT_ID_FIELD } from './constants';
import { isGraphQLName } from './gql-literal';
import { artifactDisplayName, artifactTableView } from './labels';
import { isSelectableFieldPath, resolvePath } from './message-mapping';
import { buildPagedQuery } from './selection-set';

/**
 * Query construction for cross-artifact search.
 *
 * There is no search endpoint: this compiles the schema's own `contains`
 * comparator into a per-type query and the caller fans them out. Be honest about
 * what that is — case-insensitive substring matching (PostgreSQL `ILIKE`), one
 * page per type, no relevance ordering, and only the types the schema marks as
 * searchable. The hook is written against an interface so a ranked backend
 * endpoint can replace it without touching the UI.
 */

/** Types searched per query. Each is one request against the cell. */
export const MAX_SEARCH_TYPES = 12;

/** Matches fetched per type. */
export const SEARCH_HITS_PER_TYPE = 10;

/** Shorter terms match nearly everything and turn the fan-out into a scan. */
export const MIN_SEARCH_TERM_LENGTH = 3;

/** One artifact type participating in a search. */
export interface SearchSource {
  graphqlType: string;
  typeLabel: string;
  rootQuery: string;
  endpoint: MobileGraphqlEndpoint;
  /** Fields matched with `contains`. */
  fields: string[];
  /** Fields selected for the result snippet. */
  displayFields: string[];
  /** Row count on the acquisition, used to rank which types are searched. */
  rowCount: number;
}

/**
 * Fields a type can be searched on.
 *
 * `searchable_fields` is the schema's UI-facing counterpart to the ingest-only
 * `rag_searchable` flag; the intersection with `filterable_fields` is what the
 * server will actually accept a `contains` on, so a stale authored name can't
 * fail the whole query.
 */
export function searchableFieldsFor(artifact: MobileUiConfigArtifact | undefined): string[] {
  const declared = artifact?.front_end?.searchable_fields ?? [];
  if (!declared.length) return [];
  const filterable = artifact?.capabilities?.filterable_fields ?? {};
  return declared.filter(
    (field) => filterable[field] === 'String' && isSelectableFieldPath(artifact, field),
  );
}

/**
 * Rank artifact types for a search, largest first.
 *
 * Row count is a crude proxy for where an answer is likely to be, but it is the
 * only signal available without a ranked index — and searching the biggest types
 * first is at least predictable, which an arbitrary schema order is not.
 */
export function planSearchSources(
  uiConfig: MobileUiConfigResponse | undefined,
  rowCounts: Map<string, number>,
  limit = MAX_SEARCH_TYPES,
): SearchSource[] {
  if (!uiConfig) return [];

  const sources: SearchSource[] = [];
  for (const [graphqlType, artifact] of Object.entries(uiConfig.artifacts)) {
    const rootQuery = artifact.front_end?.graphql_root_query;
    if (!rootQuery || !isGraphQLName(rootQuery)) continue;
    const fields = searchableFieldsFor(artifact);
    if (!fields.length) continue;

    // A type with no rows on this acquisition can only return nothing.
    const rowCount = rowCounts.get(graphqlType) ?? 0;
    if (rowCount <= 0) continue;

    sources.push({
      graphqlType,
      typeLabel: artifactDisplayName(artifact, graphqlType),
      rootQuery,
      endpoint: artifact.endpoint,
      fields,
      displayFields: displayFieldsFor(artifact, fields),
      rowCount,
    });
  }

  return sources.sort((a, b) => b.rowCount - a.rowCount).slice(0, limit);
}

/** Fields shown on a hit: the searched fields plus a little curated context. */
function displayFieldsFor(
  artifact: MobileUiConfigArtifact,
  searchFields: string[],
): string[] {
  const fields = new Set(searchFields);
  for (const field of (artifactTableView(artifact)?.default_columns ?? []).slice(0, 3)) {
    if (isSelectableFieldPath(artifact, field)) fields.add(field);
  }
  return [...fields];
}

/** One page of matches for a single type. */
export function buildSearchQuery(args: {
  source: SearchSource;
  mobileAcquisitionId: number;
  term: string;
  limit: number;
}): string | null {
  const { source, term } = args;
  return buildPagedQuery({
    operationName: 'ArtifactSearch',
    rootQuery: source.rootQuery,
    mobileAcquisitionId: args.mobileAcquisitionId,
    offset: 0,
    limit: args.limit,
    fields: [ARTIFACT_ID_FIELD, ...source.displayFields],
    where: { OR: source.fields.map((field) => ({ [field]: { contains: term } })) },
  });
}

export interface SearchHit {
  id: string;
  graphqlType: string;
  typeLabel: string;
  /** Best matching field's value, trimmed around the match. */
  snippet: string;
  /** Field the snippet came from. */
  field: string | null;
  row: Record<string, unknown>;
}

export interface SearchGroup {
  graphqlType: string;
  typeLabel: string;
  hits: SearchHit[];
  /** Rows the server reports as matching, which can exceed the hits fetched. */
  total: number;
}

/** Map one type's rows into hits, picking the field that actually matched. */
export function mapSearchHits(
  rows: Record<string, unknown>[],
  source: SearchSource,
  term: string,
): SearchHit[] {
  const needle = term.toLowerCase();

  return rows.map((row, index) => {
    let field: string | null = null;
    let value = '';

    for (const candidate of source.fields) {
      const raw = resolvePath(row, candidate);
      if (raw == null) continue;
      const text = String(raw);
      if (text.toLowerCase().includes(needle)) {
        field = candidate;
        value = text;
        break;
      }
    }

    // The server matched on some field; if none of them contain the term as
    // plain text (a normalized column, say), fall back to the first value shown.
    if (!field) {
      for (const candidate of source.displayFields) {
        const raw = resolvePath(row, candidate);
        if (raw == null || raw === '') continue;
        field = candidate;
        value = String(raw);
        break;
      }
    }

    return {
      id: String(resolvePath(row, ARTIFACT_ID_FIELD) ?? index),
      graphqlType: source.graphqlType,
      typeLabel: source.typeLabel,
      snippet: snippetAround(value, needle),
      field,
      row,
    };
  });
}

/** Trim a long value to a window around the match. */
export function snippetAround(value: string, needle: string, radius = 60): string {
  const collapsed = value.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= radius * 2) return collapsed;

  const at = collapsed.toLowerCase().indexOf(needle);
  if (at === -1) return `${collapsed.slice(0, radius * 2)}…`;

  const start = Math.max(0, at - radius);
  const end = Math.min(collapsed.length, at + needle.length + radius);
  return `${start > 0 ? '…' : ''}${collapsed.slice(start, end)}${
    end < collapsed.length ? '…' : ''
  }`;
}
